/** Phase 235 — Live status-bar theme-color updates.
 * Reads the *resolved* theme (light/dark) and updates the meta `theme-color`
 * tag so installed PWAs match the in-app theme on the OS chrome
 * (Android pull-down bar, iOS Safari URL bar / standalone mode).
 *
 * Without this hook, the page is shipped with @media-based static
 * theme-color tags that only react to OS preference, not the user's
 * in-app toggle. */

import { useEffect } from "react";
import { useTheme } from "../contexts/ThemeContext";

const FALLBACK = { light: "#ffffff", dark: "#191A12" };

/* The status bar should be the colour of whatever top bar is on screen — Fixlr's, or a client's brand-tinted one. */
function topbarColour(resolved) {
  const raw = getComputedStyle(document.documentElement).getPropertyValue("--topbar").trim();
  const m = raw.match(/^([\d.]+)\s+([\d.]+)%\s+([\d.]+)%$/);
  if (!m) return FALLBACK[resolved] || FALLBACK.light;
  const [h, s, l] = [Number(m[1]), Number(m[2]) / 100, Number(m[3]) / 100];
  const k = (n) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n) => Math.round(255 * (l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))));
  return `#${[f(0), f(8), f(4)].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
}

export function useStatusBarTheme() {
  const { theme } = useTheme();

  useEffect(() => {
    const apply = () => {
    const resolved = theme === "system"
      ? (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light")
      : theme;
    const colour = topbarColour(resolved);

    // Strip any media-based tags so a single resolved value wins
    document.head
      .querySelectorAll('meta[name="theme-color"][media]')
      .forEach((el) => el.parentNode.removeChild(el));

    let tag = document.head.querySelector('meta[name="theme-color"]:not([media])');
    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute("name", "theme-color");
      document.head.appendChild(tag);
    }
    tag.setAttribute("content", colour);

    // iOS standalone status-bar style — black-translucent works for both
    // themes since our content has its own background colour
    let appleTag = document.head.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
    if (!appleTag) {
      appleTag = document.createElement("meta");
      appleTag.setAttribute("name", "apple-mobile-web-app-status-bar-style");
      document.head.appendChild(appleTag);
    }
    appleTag.setAttribute("content", resolved === "dark" ? "black-translucent" : "default");
    };
    apply();
    // a client's brand tint (PortalTheme) can land after mount — follow it
    window.addEventListener("fixlr:brand", apply);
    return () => window.removeEventListener("fixlr:brand", apply);
  }, [theme]);
}
